import { useEffect, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { ArrowLeft, Play, FileText, CheckCircle2, XCircle, AlertTriangle, HelpCircle } from 'lucide-react'
import {
  getBatch,
  processBatch,
  getBatchReport,
  getBatchSummary,
  type AuditBatch,
  type ChartRecord,
  type BatchReport,
  type BatchSummary,
} from '../../lib/services/chartAudit'

const SCORE_LABELS: Record<number, { label: string; color: string }> = {
  1: { label: 'Well Maintained', color: 'text-green-700 bg-green-50' },
  2: { label: 'Needs Improvement', color: 'text-amber-700 bg-amber-50' },
  3: { label: 'Not Maintained', color: 'text-red-700 bg-red-50' },
}

const SCOPE_LABELS: Record<string, { label: string; color: string }> = {
  limited: { label: 'Limited', color: 'text-blue-700 bg-blue-50' },
  pattern: { label: 'Pattern', color: 'text-amber-700 bg-amber-50' },
  widespread: { label: 'Widespread', color: 'text-red-700 bg-red-50' },
}

const RESULT_ICONS: Record<string, { icon: typeof CheckCircle2; color: string }> = {
  pass: { icon: CheckCircle2, color: 'text-green-500' },
  fail: { icon: XCircle, color: 'text-red-500' },
  incomplete: { icon: HelpCircle, color: 'text-amber-500' },
  na: { icon: AlertTriangle, color: 'text-slate-400' },
}

export function ChartAuditBatchPage() {
  const { batchId } = useParams<{ batchId: string }>()
  const [batch, setBatch] = useState<AuditBatch | null>(null)
  const [records, setRecords] = useState<ChartRecord[]>([])
  const [report, setReport] = useState<BatchReport | null>(null)
  const [summary, setSummary] = useState<BatchSummary | null>(null)
  const [loading, setLoading] = useState(true)
  const [processing, setProcessing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState<'charts' | 'summary'>('charts')

  useEffect(() => {
    if (!batchId) return
    let cancelled = false
    async function load() {
      try {
        setLoading(true)
        const result = await getBatch(batchId!)
        if (cancelled) return
        setBatch(result.batch)
        setRecords(result.records)

        if (result.batch.batch_status === 'completed') {
          const [rpt, sum] = await Promise.all([
            getBatchReport(batchId!),
            getBatchSummary(batchId!),
          ])
          if (!cancelled) {
            setReport(rpt)
            setSummary(sum)
          }
        }
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load batch')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => { cancelled = true }
  }, [batchId])

  const handleProcess = async () => {
    if (!batchId) return
    setProcessing(true)
    try {
      await processBatch(batchId)
      // Reload
      const result = await getBatch(batchId)
      setBatch(result.batch)
      setRecords(result.records)
      if (result.batch.batch_status === 'completed') {
        const [rpt, sum] = await Promise.all([
          getBatchReport(batchId),
          getBatchSummary(batchId),
        ])
        setReport(rpt)
        setSummary(sum)
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Processing failed')
    } finally {
      setProcessing(false)
    }
  }

  if (loading) {
    return <div className="py-12 text-center text-sm text-slate-500">Loading batch...</div>
  }

  if (error) {
    return <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">{error}</div>
  }

  if (!batch) return null

  const scoreDist = batch.score_distribution
  const scopeClass = batch.scope_classifications

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="v11-workspace-header flex items-center gap-4">
        <Link to="/chart-audit" className="rounded-lg p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-600">
          <ArrowLeft size={20} />
        </Link>
        <div className="flex-1">
          <h1 className="text-xl font-bold text-slate-900">Audit Batch {batch.id.slice(0, 8)}</h1>
          <p className="mt-0.5 text-xs text-slate-500">
            {batch.processed_count}/{batch.file_count} charts &middot; {batch.intake_path.toUpperCase()} &middot;{' '}
            {new Date(batch.created_at).toLocaleDateString()}
          </p>
        </div>
        {batch.batch_status === 'pending' && records.length > 0 && (
          <button
            onClick={handleProcess}
            disabled={processing}
            className="flex items-center gap-2 rounded-lg bg-[var(--brand-primary)] px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:opacity-90 disabled:opacity-50"
          >
            <Play size={16} />
            {processing ? 'Processing...' : 'Run Audit'}
          </button>
        )}
      </div>

      {/* Score summary cards (completed batches) */}
      {batch.batch_status === 'completed' && scoreDist && (
        <div className="grid gap-4 sm:grid-cols-4">
          <div className="v11-kpi-card">
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Sample Size</p>
            <p className="mt-1 text-2xl font-bold text-slate-900">{batch.processed_count}</p>
          </div>
          <div className="v11-kpi-card v11-kpi-accent-teal">
            <p className="text-xs font-semibold uppercase tracking-wider text-green-600">Well Maintained</p>
            <p className="mt-1 text-2xl font-bold text-green-700">{scoreDist['1'] ?? 0}</p>
          </div>
          <div className="v11-kpi-card v11-kpi-accent-gold">
            <p className="text-xs font-semibold uppercase tracking-wider text-amber-600">Needs Improvement</p>
            <p className="mt-1 text-2xl font-bold text-amber-700">{scoreDist['2'] ?? 0}</p>
          </div>
          <div className="v11-kpi-card v11-kpi-accent-rose">
            <p className="text-xs font-semibold uppercase tracking-wider text-red-600">Not Maintained</p>
            <p className="mt-1 text-2xl font-bold text-red-700">{scoreDist['3'] ?? 0}</p>
          </div>
        </div>
      )}

      {/* Scope classifications */}
      {batch.batch_status === 'completed' && scopeClass && Object.keys(scopeClass).length > 0 && (
        <div className="v11-section p-4">
          <h3 className="text-sm font-bold text-slate-700">Scope Classifications</h3>
          <div className="mt-3 flex flex-wrap gap-2">
            {Object.entries(scopeClass).map(([ruleId, scope]) => {
              const style = SCOPE_LABELS[scope] || SCOPE_LABELS.limited
              return (
                <span key={ruleId} className={`rounded-full px-3 py-1 text-xs font-semibold ${style.color}`}>
                  {ruleId}: {style.label}
                </span>
              )
            })}
          </div>
        </div>
      )}

      {/* Tab bar */}
      {batch.batch_status === 'completed' && (
        <div className="flex gap-1 border-b border-slate-200">
          <button
            onClick={() => setActiveTab('charts')}
            className={`px-4 py-2.5 text-sm font-semibold ${activeTab === 'charts' ? 'border-b-2 border-[var(--brand-primary)] text-[var(--brand-primary)]' : 'text-slate-500 hover:text-slate-700'}`}
          >
            Chart Details
          </button>
          <button
            onClick={() => setActiveTab('summary')}
            className={`px-4 py-2.5 text-sm font-semibold ${activeTab === 'summary' ? 'border-b-2 border-[var(--brand-primary)] text-[var(--brand-primary)]' : 'text-slate-500 hover:text-slate-700'}`}
          >
            Governing Body Summary
          </button>
        </div>
      )}

      {/* Charts tab */}
      {activeTab === 'charts' && (
        <div className="space-y-4">
          {report?.charts.map((chart) => {
            const scoreInfo = chart.chart_score ? SCORE_LABELS[chart.chart_score] : null
            return (
              <div key={chart.chart_token} className="v11-dashboard-card">
                <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
                  <div className="flex items-center gap-3">
                    <FileText size={18} className="text-slate-400" />
                    <div>
                      <span className="font-semibold text-slate-900">{chart.chart_token}</span>
                      {chart.procedure_type && (
                        <span className="ml-2 text-xs text-slate-500">{chart.procedure_type}</span>
                      )}
                    </div>
                  </div>
                  {scoreInfo && (
                    <span className={`rounded-full px-3 py-1 text-xs font-semibold ${scoreInfo.color}`}>
                      {scoreInfo.label}
                    </span>
                  )}
                </div>
                <div className="divide-y divide-slate-50">
                  {chart.findings.map((f) => {
                    const ri = RESULT_ICONS[f.result] || RESULT_ICONS.na
                    const Icon = ri.icon
                    return (
                      <div key={f.rule_id} className="flex items-start gap-3 px-4 py-2.5">
                        <Icon size={16} className={`mt-0.5 shrink-0 ${ri.color}`} />
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-bold text-slate-600">{f.rule_id}</span>
                            <span className="text-xs text-slate-500">{f.element_name}</span>
                          </div>
                          {f.finding_statement && (
                            <p className="mt-0.5 text-xs text-slate-700">{f.finding_statement}</p>
                          )}
                          {f.corrective_action_prompt && (
                            <p className="mt-1 text-xs italic text-blue-600">{f.corrective_action_prompt}</p>
                          )}
                        </div>
                        <span className="shrink-0 text-[10px] font-semibold uppercase text-slate-400">
                          {f.regulatory_citation}
                        </span>
                      </div>
                    )
                  })}
                </div>
              </div>
            )
          })}

          {/* Pending/empty states */}
          {batch.batch_status === 'pending' && records.length === 0 && (
            <div className="rounded-xl border-2 border-dashed border-slate-200 py-12 text-center">
              <p className="text-sm font-semibold text-slate-500">No charts ingested yet</p>
              <p className="mt-1 text-xs text-slate-400">Upload chart data via the API to populate this batch</p>
            </div>
          )}

          {batch.batch_status === 'pending' && records.length > 0 && (
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-center text-sm text-amber-700">
              {records.length} charts ready for audit. Click "Run Audit" to evaluate against CMS §416.47(b) rules.
            </div>
          )}
        </div>
      )}

      {/* Summary tab */}
      {activeTab === 'summary' && summary && (
        <div className="space-y-6">
          <div className="v11-section p-6">
            <h2 className="text-lg font-bold text-slate-900">Governing Body Chart Audit Summary</h2>
            <p className="mt-1 text-xs text-slate-500">
              Prepared for governing body meeting minutes — {new Date(summary.audit_date).toLocaleDateString()}
            </p>

            <div className="mt-6 space-y-4">
              <div>
                <h3 className="text-sm font-bold text-slate-700">Audit Overview</h3>
                <p className="mt-1 text-sm text-slate-600">
                  Sample of {summary.sample_size} charts audited against 12 CMS §416.47(b) medical records requirements.
                </p>
              </div>

              {/* Per-rule aggregates */}
              <div>
                <h3 className="text-sm font-bold text-slate-700">Results by Rule</h3>
                <div className="mt-2 overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b border-slate-100 text-left text-slate-500">
                        <th className="px-3 py-2">Rule</th>
                        <th className="px-3 py-2">Pass</th>
                        <th className="px-3 py-2">Fail</th>
                        <th className="px-3 py-2">Incomplete</th>
                        <th className="px-3 py-2">N/A</th>
                        <th className="px-3 py-2">Scope</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-50">
                      {Object.entries(summary.rule_aggregates).map(([ruleId, agg]) => {
                        const scope = summary.scope_classifications?.[ruleId]
                        const scopeStyle = scope ? SCOPE_LABELS[scope] : null
                        return (
                          <tr key={ruleId}>
                            <td className="px-3 py-2 font-semibold text-slate-700">{ruleId}</td>
                            <td className="px-3 py-2 text-green-600">{agg.pass}</td>
                            <td className="px-3 py-2 text-red-600">{agg.fail}</td>
                            <td className="px-3 py-2 text-amber-600">{agg.incomplete}</td>
                            <td className="px-3 py-2 text-slate-400">{agg.na}</td>
                            <td className="px-3 py-2">
                              {scopeStyle ? (
                                <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${scopeStyle.color}`}>
                                  {scopeStyle.label}
                                </span>
                              ) : (
                                <span className="text-slate-400">—</span>
                              )}
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* PIP recommendations */}
              {summary.pip_recommendations.length > 0 && (
                <div>
                  <h3 className="text-sm font-bold text-red-700">PIP Recommendations</h3>
                  <div className="mt-2 space-y-2">
                    {summary.pip_recommendations.map((pip) => (
                      <div key={pip.rule_id} className="rounded-lg border border-red-200 bg-red-50 p-3">
                        <div className="flex items-center gap-2">
                          <AlertTriangle size={14} className="text-red-500" />
                          <span className="text-xs font-bold text-red-700">{pip.rule_id} — {pip.scope.toUpperCase()}</span>
                        </div>
                        <p className="mt-1 text-xs text-red-600">
                          Performance Improvement Project recommended for {pip.standard}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Trend */}
              <div>
                <h3 className="text-sm font-bold text-slate-700">Trend</h3>
                {summary.trend.message ? (
                  <p className="mt-1 text-sm italic text-slate-500">{summary.trend.message}</p>
                ) : (
                  <p className="mt-1 text-sm text-slate-600">
                    Prior batch: {summary.trend.prior_date ? new Date(summary.trend.prior_date).toLocaleDateString() : 'N/A'}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
